import React, { createContext, useContext, useState, useEffect } from 'react'
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '../config/firebase'

const SETTINGS_DOC = 'app-settings'
const SETTINGS_COLLECTION = 'settings'

type PlaybackMode = 'manual' | 'auto'

interface SettingsState {
  playbackMode: PlaybackMode
}

const defaultSettings: SettingsState = {
  playbackMode: 'manual',
}

interface SettingsContextValue {
  settings: SettingsState
  loading: boolean
  error: string
  updateSettings: (
    newSettings: Partial<SettingsState>,
  ) => Promise<{ success: boolean; error?: string }>
  updatePlaybackMode: (
    mode: PlaybackMode,
  ) => Promise<{ success: boolean; error?: string }>
  fetchSettings: () => Promise<void>
}

const SettingsContext = createContext<SettingsContextValue | null>(null)

export const useSettings = () => {
  const context = useContext(SettingsContext)
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider')
  }
  return context
}

interface SettingsProviderProps {
  children: React.ReactNode
}

export const SettingsProvider = ({ children }: SettingsProviderProps) => {
  const [settings, setSettings] = useState<SettingsState>(defaultSettings)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const fetchSettings = async () => {
    if (!db) {
      setSettings(defaultSettings)
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      const docRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC)
      const snapshot = await getDoc(docRef)
      if (snapshot.exists()) {
        const data = snapshot.data()
        setSettings({
          playbackMode: data.playbackMode ?? defaultSettings.playbackMode,
        })
      } else {
        setSettings(defaultSettings)
      }
    } catch (err) {
      setError('설정을 불러오는데 실패했습니다: ' + (err as Error).message)
      setSettings(defaultSettings)
    } finally {
      setLoading(false)
    }
  }

  const updateSettings = async (newSettings: Partial<SettingsState>) => {
    if (!db) {
      setError('Firebase가 설정되지 않았습니다.')
      return { success: false, error: 'Firebase가 설정되지 않았습니다.' }
    }
    try {
      setLoading(true)
      setError('')
      const mergedSettings = { ...settings, ...newSettings }
      await setDoc(
        doc(db, SETTINGS_COLLECTION, SETTINGS_DOC),
        { ...mergedSettings, updatedAt: serverTimestamp() },
        { merge: true },
      )
      setSettings(mergedSettings)
      return { success: true }
    } catch (err) {
      setError('설정 저장에 실패했습니다: ' + (err as Error).message)
      return { success: false, error: (err as Error).message }
    } finally {
      setLoading(false)
    }
  }

  const updatePlaybackMode = async (mode: PlaybackMode) => {
    return await updateSettings({ playbackMode: mode })
  }

  useEffect(() => {
    fetchSettings()
  }, [])

  const value = {
    settings,
    loading,
    error,
    updateSettings,
    updatePlaybackMode,
    fetchSettings,
  }

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  )
}
